import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

interface Props {
  id: string;
  name: string;
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  label: React.ReactNode;
  description?: string;
  disabled?: boolean;
}

const FormRadio: React.FunctionComponent<Props> = ({
  id,
  name,
  value,
  checked,
  onChange,
  label,
  description,
  disabled = false,
}) => {
  const handleClick = (event) => {
    if (disabled) return;
    onChange(event.currentTarget.value);
  };

  return (
    <Styled.Component hasDescription={!!description}>
      <input
        id={id}
        name={name}
        type="radio"
        value={value}
        checked={checked}
        onChange={handleClick}
        disabled={disabled}
      />
      <label htmlFor={id}>
        {label}
        {description && <Styled.Description>{description}</Styled.Description>}
      </label>
    </Styled.Component>
  );
};

export default FormRadio;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Component = styled.div((props: any) => {
  const { theme, hasDescription } = props;
  const t: any = theme;
  return css`
    label: FormRadio;
    ${t.h(8)}
    display: flex;
    align-items: center;
    position: relative;

    [type="radio"]:not(:checked),
    [type="radio"]:checked {
      position: absolute;
      left: 0;
      opacity: 0.01;
    }
    [type="radio"]:not(:checked) + label,
    [type="radio"]:checked + label {
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
      position: relative;
      padding-left: 1.625rem;
      padding-right: 0.5rem;
      cursor: pointer;
      transition: color 0.2s ease;
      height: 100%;
      display: flex;
      align-items: center;
    }

    [type="radio"]:checked + label {
      color: ${t.dark ? t.color.gray[200] : t.color.black};
    }

    [type="radio"]:not(:checked) + label::before,
    [type="radio"]:checked + label::before {
      content: "";
      position: absolute;
      left: 0;
      width: 1.125rem;
      height: 1.125rem;
      box-sizing: border-box;
      border: 1px solid ${t.dark ? "transparent" : t.color.gray[300]};
      border-radius: 50%;
      background: ${t.dark ? "rgba(255,255,255,0.07)" : t.color.white};
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }

    [type="radio"]:not(:checked) + label::after,
    [type="radio"]:checked + label::after {
      content: "";
      position: absolute;
      left: 0.3125rem;
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 50%;
      background-color: ${t.dark ? t.color.gray[200] : t.color.black};
      transition: opacity 0.15s ease;
    }

    [type="radio"]:not(:checked) + label::after {
      opacity: 0;
    }

    [type="radio"]:checked + label::after {
      opacity: 1;
    }

    [type="radio"]:checked + label::before {
      border-color: ${t.dark ? t.color.gray[300] : t.color.black};
    }

    [type="radio"]:disabled:not(:checked) + label::before,
    [type="radio"]:disabled:checked + label::before {
      background-color: ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    }

    [type="radio"]:disabled + label {
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
      cursor: default;
    }

    [type="radio"]:checked:hover + label::before,
    [type="radio"]:not(:checked):hover + label::before {
      border-color: ${t.dark ? t.color.gray[600] : t.color.gray[400]};
    }

    [type="radio"]:checked:hover + label,
    [type="radio"]:not(:checked):hover + label {
      color: ${t.dark ? t.color.gray[200] : t.color.black};
    }

    [type="radio"]:checked:focus + label::before,
    [type="radio"]:not(:checked):focus + label::before {
      border-color: ${t.dark ? t.color.gray[500] : t.color.gray[400]};
      box-shadow: 0 0 0 2px ${t.dark ? "none" : t.color.gray[300]};
    }

    ${hasDescription &&
    css`
      height: auto;
      align-items: flex-start;

      [type="radio"]:not(:checked) + label,
      [type="radio"]:checked + label {
        flex-direction: column;
        align-items: flex-start;
        height: auto;
        padding-top: 3px;
        color: ${t.dark ? t.color.gray[200] : t.color.black};
      }

      [type="radio"]:not(:checked) + label::before,
      [type="radio"]:checked + label::before {
        top: 0.125rem;
      }

      [type="radio"]:not(:checked) + label::after,
      [type="radio"]:checked + label::after {
        top: 0.4375rem;
      }
    `}
  `;
});

Styled.Description = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: FormRadio_Description;
    font-size: 0.75rem;
    line-height: 1.45;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    margin-top: 0.125rem;
  `;
});
